import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { changePassword } from "@/lib/authApi";
import { PrivacySettingsTab } from "@/components/settings/PrivacySettingsTab";
import { PlanSettingsTab } from "@/components/settings/PlanSettingsTab";
import { DeleteAccountModal } from "@/components/settings/DeleteAccountModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StandardPageShell } from "@/components/layout/StandardPageShell";
import { logger } from "@/lib/logger";

type SettingsTab = "general" | "security" | "plan" | "privacy";

const SETTINGS_TABS: readonly SettingsTab[] = [
  "general",
  "security",
  "plan",
  "privacy",
];

function getSettingsTabFromHash(hash: string): SettingsTab {
  const normalized = hash.replace(/^#/, "") as SettingsTab;
  return SETTINGS_TABS.includes(normalized) ? normalized : "security";
}

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<SettingsTab>(() =>
    typeof globalThis !== "undefined" &&
    "location" in globalThis &&
    typeof globalThis.location.hash === "string"
      ? getSettingsTabFromHash(globalThis.location.hash)
      : "security",
  );

  useEffect(() => {
    setActiveTab(getSettingsTabFromHash(location.hash));
  }, [location.hash]);

  const handleTabChange = useCallback(
    (value: string) => {
      const next = value as SettingsTab;
      setActiveTab(next);
      navigate({ pathname: location.pathname, hash: next }, { replace: true });
    },
    [navigate, location.pathname],
  );

  // Password state
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Delete account modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error(
        t("settings.password.mismatch", {
          defaultValue: "Passwords do not match",
        }),
      );
      return;
    }

    if (newPassword.length < 8) {
      toast.error(
        t("settings.password.tooShort", {
          defaultValue: "Password must be at least 8 characters",
        }),
      );
      return;
    }

    setIsLoading(true);
    try {
      const result = await changePassword(oldPassword, newPassword);
      if (result.success) {
        toast.success(
          t("settings.password.success", {
            defaultValue: "Password changed successfully",
          }),
        );
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        toast.error(
          result.message ||
            t("settings.password.error", {
              defaultValue: "Failed to change password",
            }),
        );
      }
    } catch (error) {
      logger.error("Password change error", error, {
        event: "settings.password_change.failed",
      });
      toast.error(
        t("settings.password.error", {
          defaultValue: "Failed to change password",
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StandardPageShell
      title={t("settings.title", { defaultValue: "Settings" })}
      headerRight={<LanguageSwitcher />}
    >
      <div className="max-w-7xl mx-auto">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <div className="scrollbar-hide -mx-1 mb-4 overflow-x-auto px-1">
            <TabsList className="w-max min-w-full sm:w-full sm:min-w-0">
              <TabsTrigger value="general">
                {t("settings.tabs.general", { defaultValue: "General" })}
              </TabsTrigger>
              <TabsTrigger value="security">
                {t("settings.tabs.security", { defaultValue: "Security" })}
              </TabsTrigger>
              <TabsTrigger value="plan">
                {t("settings.tabs.plan", { defaultValue: "Plan" })}
              </TabsTrigger>
              <TabsTrigger value="privacy">
                {t("consent.privacySettings.tabLabel", {
                  defaultValue: "Privacy",
                })}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t("settings.general.title", {
                    defaultValue: "General Information",
                  })}
                </CardTitle>
                <CardDescription>
                  {t("settings.general.description", {
                    defaultValue: "View your account details.",
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-w-md">
                  <div className="grid gap-2">
                    <Label>
                      {t("settings.general.username", {
                        defaultValue: "Username",
                      })}
                    </Label>
                    <Input value={user?.username || ""} disabled />
                  </div>
                  <div className="grid gap-2">
                    <Label>
                      {t("settings.general.email", { defaultValue: "Email" })}
                    </Label>
                    <Input value={user?.email || ""} disabled />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div
              className="mt-6 rounded-lg border border-destructive/40 p-4 sm:mt-8 sm:p-6"
              data-testid="danger-zone"
            >
              <h2 className="text-base font-semibold text-destructive mb-1">
                {t("settings.dangerZone.title", {
                  defaultValue: "Danger Zone",
                })}
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                {t("settings.dangerZone.description", {
                  defaultValue:
                    "Irreversible actions that permanently affect your account.",
                })}
              </p>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteModal(true)}
                data-testid="delete-account-button"
              >
                {t("settings.deleteAccount.trigger", {
                  defaultValue: "Delete Account",
                })}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="plan" id="plan" className="scroll-mt-20">
            <PlanSettingsTab />
          </TabsContent>

          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t("settings.password.title", {
                    defaultValue: "Change Password",
                  })}
                </CardTitle>
                <CardDescription>
                  {t("settings.password.description", {
                    defaultValue:
                      "Update your password to keep your account secure.",
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={handlePasswordChange}
                  className="space-y-4 max-w-md"
                >
                  <div className="space-y-2">
                    <Label htmlFor="current-password">
                      {t("settings.password.current", {
                        defaultValue: "Current Password",
                      })}
                    </Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">
                      {t("settings.password.new", {
                        defaultValue: "New Password",
                      })}
                    </Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">
                      {t("settings.password.confirm", {
                        defaultValue: "Confirm Password",
                      })}
                    </Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading
                      ? t("common.processing", {
                          defaultValue: "Processing...",
                        })
                      : t("settings.password.submit", {
                          defaultValue: "Update Password",
                        })}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="privacy">
            <PrivacySettingsTab />
          </TabsContent>
        </Tabs>
      </div>

      <DeleteAccountModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
      />
    </StandardPageShell>
  );
}
